import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogContent,
  Switch,
  TableSortLabel,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import NavBar from "../NavBar";
import api from "../../axiosInstance";
import { useTheme } from "@emotion/react";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { toast, ToastContainer } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import NotificationModal from "../NotificationModal";

// date and time formating
const formatDateTime = (dateString) => {
  if (!dateString) return "-";

  const date = new Date(dateString);

  // Format date
  const formattedDate = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Format 12-hour time
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} ${formattedTime}`;
};

const formatDateList = (arr) => {
  if (!arr || arr.length === 0) return "-";
  return arr.map((d) => formatDateTime(d)).join(", ");
};
// Column config and visibility state
const allColumns = [
  {
    key: "id",
    label: "ID",
    cellCss: { align: "center", minWidth: "50px" },
    getValue: (r) => r.id || "-",
  },
  {
    key: "title",
    label: "Title",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.title || "-",
  },
  {
    key: "message",
    label: "Message",
    cellCss: {
      align: "center",

      maxWidth: 300,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    getValue: (r) => r.message || "-",
  },
  {
    key: "image",
    label: "Image",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.image || "-",
  },

  {
    key: "targetType",
    label: "Target Type",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.targetType || "-",
  },
  {
    key: "creationDate",
    label: "Creation Date",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => formatDateTime(r.creationDate) || "-",
  },
  {
    key: "scheduled_date_and_time",
    label: "Scheduled date & time",
    cellCss: { align: "center", minWidth: "200px" },
    getValue: (r) => formatDateList(r.scheduled_date_and_time) || "-",
  },
  {
    key: "recurDatesAndTimes",
    label: "Recurring Dates & Times",
    cellCss: { align: "center", minWidth: "200px" },
    getValue: (r) => formatDateList(r.recurDatesAndTimes) || "-",
  },
  {
    key: "sentStatus",
    label: "Sent Status",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.sentStatus || "-",
  },
  {
    key: "totalUsers",
    label: "Total Users",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.totalUsers || "0",
  },
  {
    key: "sentCount",
    label: "Sent Count",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.sentCount || "0",
  },
  {
    key: "notSentCount",
    label: "Not Sent Count",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.notSentCount || "0",
  },
];
const defaultVisibleKeys = new Set([
  "id",
  "title",
  "message",
  "image",
  "targetType",
  "creationDate",
  "sentStatus",
]);

const STORAGE_KEY = "NotificationListVisibleColumns";
function NotificationsList() {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notificationsList, setNotificationsList] = useState([]);
  const [states, setStates] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const handleNewClose = () => setOpenNew(false);
  const [selectedstates, setSelectedStates] = useState("1" || "");
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("banner_management") || "";
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openImage, setOpenImage] = useState(null);
  const [notificationsEdit, setNotificationsEdit] = useState(null);
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [retryingIds, setRetryingIds] = useState(new Set());
  const [totalElements, setTotalElements] = useState(0);

  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("banner_management", searchQuery);
  }, [searchQuery]);
  // Sorting logic
  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };
  const filteredData = notificationsList.filter((banner) =>
    banner.title?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const sortedData = useMemo(() => {
    if (!filteredData) return [];

    return [...filteredData].sort((a, b) => {
      if (!orderBy) return 0;

      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredData, orderBy, order]);

  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };
  const handleChange = (event) => {
    setSelectedStates(event.target.value);
  };

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch notifications with pagination
  const fetchNotifications = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        page,
        size: rowsPerPage,
      };

      if (debouncedSearch && debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const response = await api.get("/api/v1/notifications/list", { params });

      if (response.data?.isError) {
        console.error("Error:", response.data.errorMessage);
        setError(response.data.errorMessage || "Failed to load notifications");
        setNotificationsList([]);
        setTotalElements(0);
      } else {
        const data = response.data?.data || response.data;
        const notifications = Array.isArray(data?.content) ? data.content : [];

        // Debug: Log notification statuses to console

        setNotificationsList(notifications);
        setTotalElements(
          typeof data?.totalElements === "number" ? data.totalElements : 0,
        );
        console.log(
          typeof data?.totalElements === "number" ? data.totalElements : 0,
        );

        setError(null);
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to load notifications. Please try again.");
      setNotificationsList([]);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch notifications whenever page, rowsPerPage, or search changes
  useEffect(() => {
    fetchNotifications();
  }, [page, rowsPerPage, debouncedSearch]);

  const handleRefresh = () => {
    fetchNotifications();
  };

  // Handle retry notification
  const handleRetryNotification = async (notificationId) => {
    if (retryingIds.has(notificationId)) return; // Prevent duplicate requests

    setRetryingIds((prev) => new Set(prev).add(notificationId));

    try {
      const response = await api.post(
        `/api/v1/notifications/${notificationId}/retry`,
      );

      // Check if response is a string (success message) or object
      const message =
        typeof response.data === "string"
          ? response.data
          : response.data?.message ||
            "Notification retry triggered successfully";

      toast.success(message, {
        position: "top-right",
        autoClose: 3000,
      });

      // Refresh the notifications list to get updated status
      await fetchNotifications();
    } catch (err) {
      console.error("Retry Error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An error occurred while retrying notification";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
    }
  };
  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper function to get boolean chip styling
  const getTargetTypeChip = (targetType) => {
    const config = {
      ALL: { color: "primary", label: "All Users" },
      SPECIFIC: { color: "secondary", label: "Specific Users" },
    };

    const chipConfig = config[targetType] || {
      color: "default",
      label: targetType || "Unknown",
    };

    return (
      <Chip
        label={chipConfig.label}
        color={chipConfig.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const getStatusChip = (status) => {
    const config = {
      SENT: { color: "success", label: "Sent" },
      PARTIAL: { color: "warning", label: "Partial" },
      NOT_SENT: { color: "error", label: "Not Sent" },
    };

    const chipConfig = config[status] || {
      color: "default",
      label: status || "Unknown",
    };

    return (
      <Chip
        label={chipConfig.label}
        color={chipConfig.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const shouldShowSendButton = (status) => {
    if (!status) return false;
    const upperStatus = status.toUpperCase();
    return upperStatus === "NOT_SENT";
  };
  //   Page nation handel changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // page nation rows handles
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };
  // console.log(sortedData);

  // // pagenation code
  // const paginatedBanner = sortedData.slice(
  //   page * rowsPerPage,
  //   page * rowsPerPage + rowsPerPage,
  // );
  // Columns Field showing obeject
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };
  // Delete The Notification list record api fetch data
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/notifications/${selectedId}`);

      // If deletion is successful, update local state update
      setNotificationsList((prev) =>
        prev.filter((row) => row.id !== selectedId),
      );
      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  // opening Adding page data in onclick
  const handleNewOpen = () => {
    setOpenNew(true);
    setNotificationsEdit([]);
  };
  // Editing page data in onclick
  const handleEditClick = (rowData) => {
    handleNewOpen();
    setNotificationsEdit(rowData);
  };
  const handleFormSuccess = (newItem) => {
    if (!newItem || !newItem.id) {
      console.warn("Invalid item received from form", newItem);
      return;
    }
    // update locally
    setNotificationsList((prev) => {
      const exists = prev.some((item) => item.id === newItem.id);
      // If editing: replace the item
      if (exists) {
        return prev.map((item) => (item.id === newItem.id ? newItem : item));
      } else {
        // If adding: push new item
        return [newItem, ...prev];
      }
    });

    setNotificationsEdit(null);
  };
  // Refreshing fetching data
  // Fetch notifications whenever page, rowsPerPage, or search changes
  useEffect(() => {
    fetchNotifications();
  }, [page, rowsPerPage, debouncedSearch]);
  // Search after it will redirect to the first page
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  return (
    <>
      <ToastContainer />

      {/* Search Section */}
      <Card sx={{ m: { xs: 1, md: "0 15px" }, boxShadow: 1 }}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" }, // stack on mobile
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mr: { md: "40px" },
          }}
        >
          {/* Search Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: { xs: "100%", md: "60%" },
              flexDirection: { xs: "column", sm: "row" }, // input & text inline on tablet+
            }}
          >
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Search Title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { md: "60%", xs: "100%" },
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: { xs: "left" },
                width: "100%",
              }}
            >
              {
                sortedData.filter((notificationsList) =>
                  notificationsList.title
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()),
                ).length
              }{" "}
              of {totalElements} Notifications
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              width: { xs: "100%", md: "25%" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              ssx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
              PaperProps={{
                sx: {
                  width: 200,
                  maxHeight: 500,
                  overflowY: "auto",
                  borderRadius: 2,
                },
              }}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={isLoading}
              sx={{
                minWidth: { xs: "30%", md: "auto" },
              }}
            >
              <RefreshIcon />
            </Button>
            <Tooltip title="Dense padding" arrow>
              <FormControlLabel
                control={
                  <Switch
                    checked={dense}
                    onChange={handleChangeDense}
                    color="primary"
                  />
                }
                sx={{
                  width: { xs: "20%", sm: "auto" },
                  justifyContent: "center",
                  mx: 0,
                }}
              />
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper
            sx={{
              width: "98%",
              mx: "auto",
              overflow: "hidden",
              boxShadow: 2,
              marginBottom: "20px",
            }}
          >
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="news table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px ",
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          ...c.cellCss,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": {
                              color: "#ffffff !important",
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#17479e",
                        color: "white",
                        fontWeight: 600,
                        minWidth: 120,
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* {console.log(paginatedBanner)} */}
                  {Array.isArray(sortedData) && sortedData.length > 0 ? (
                    sortedData
                      .filter(
                        (news) =>
                          !searchQuery ||
                          news.title
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                      )
                      .map((row) => (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                            },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {visibleColumnDefs.map((c) => (
                            <TableCell
                              key={c.key}
                              sx={{ padding: "10px 10px", ...c.cellCss }}
                            >
                              {(() => {
                                const value = c.getValue(row);
                                const key = c.key;

                                // Optional debug

                                if (key === "targetType") {
                                  return getTargetTypeChip(value);
                                }

                                if (key === "sentStatus") {
                                  return getStatusChip(value);
                                }

                                if (key === "image") {
                                  return key === "image" ? (
                                    <img
                                      src={value}
                                      alt="preview"
                                      style={{
                                        width: "100px",
                                        height: "auto",
                                        borderRadius: "3px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => setOpenImage(value)}
                                    />
                                  ) : (
                                    value // show fallback text if not valid image
                                  );
                                }

                                return value;
                              })()}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              align: "center",
                              textAlign: "center",
                              padding: "10px 10px",
                              minWidth: "120px",
                              position: "sticky",
                              right: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              {shouldShowSendButton(row.sentStatus) ? (
                                <Tooltip title="Retry sending notification">
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() =>
                                      handleRetryNotification(row.id)
                                    }
                                    disabled={retryingIds.has(row.id)}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor: "primary.light",
                                        color: "white",
                                      },
                                    }}
                                  >
                                    {retryingIds.has(row.id) ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <SendIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  -
                                </Typography>
                              )}
                              <Tooltip
                                title="Edit Notification"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditClick(row)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "primary.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title="Delete Notification"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  color="error"
                                  onClick={() => handleClickOpen(row.id)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "error.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            {searchQuery
                              ? "No Notifications found matching your search"
                              : "No Notifications available"}
                          </Typography>
                          {searchQuery && (
                            <Button
                              variant="outlined"
                              onClick={() => setSearchQuery("")}
                              startIcon={<SearchIcon />}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}
      <NotificationModal
        notificationEdit={notificationsEdit}
        // selectedUsersNotifications={pushnotifications || []}
        open={openNew}
        onClose={handleNewClose}
        onSuccess={handleFormSuccess}
      />
      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this news item? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <Dialog
        open={!!openImage}
        onClose={() => setOpenImage(null)}
        maxWidth="md"
      >
        <DialogContent sx={{ padding: "10px" }}>
          <Box position="relative">
            <IconButton
              aria-label="close"
              onClick={() => setOpenImage(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={openImage}
              alt="Preview"
              style={{ width: "100%", height: "auto", borderRadius: 4 }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default NotificationsList;
